import React, { useState, useEffect, useContext, useMemo } from 'react';
import { MDBRow, MDBCol, MDBCard, MDBCardBody, MDBCardImage, MDBCardTitle, MDBCardText, MDBBtn, MDBIcon, MDBInput, MDBSpinner, MDBDropdown, MDBDropdownToggle, MDBDropdownMenu, MDBDropdownItem } from 'mdb-react-ui-kit';
import { generateClient } from 'aws-amplify/api';
import { useTranslation } from 'react-i18next';
import { Link } from "react-router-dom";
import { getLocalizedText } from "../../utils/localizedText";
import { useIndustry } from "../../utils/industryContextHook";
import { transformMoney } from "../../utils/money";
import { UserContext } from "../../App";
import { captureError } from "../../utils/sentryHelpers";
import {
  LOUNGES_2026_EVENT_ID,
} from "../lectures/insightAccessConfig";
import "./Shop.scss";

// Events bei denen für das `event_<id>`-Targeting zusätzlich ein TicketScan
// erforderlich ist (= User war tatsächlich auf der Messe).
// Andere Events behalten die alte Logik (Ticket-Besitz reicht).
const SCAN_REQUIRED_EVENT_IDS = [LOUNGES_2026_EVENT_ID];

// Reihenfolge der Abschnitte im Shop: erst Angebote für Personen, dann für Unternehmen.
// Eine Kategorie mit beiden customerTypes erscheint in beiden Abschnitten.
const AUDIENCES = ["consumer", "business"];

const COMPANY_IMAGE_BASE_URL = "https://iec-cp-public.s3.eu-central-1.amazonaws.com/companyImages/";

const client = generateClient();

const stripHtml = (str) => (str || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const formatEventDates = (event, language) => {
  if (!event?.startDate) return "";
  const start = new Date(event.startDate);
  const end = event.endDate ? new Date(event.endDate) : start;
  const format = new Intl.DateTimeFormat(language || "de", { day: "numeric", month: "long", year: "numeric" });
  try {
    return start.toDateString() === end.toDateString() ? format.format(start) : format.formatRange(start, end);
  } catch (e) {
    return format.format(start);
  }
};

const companyImageUrl = (company) => company?.image?.main?.fileName
  ? COMPANY_IMAGE_BASE_URL + company.image.main.fileName
  : "//cdn.eu.com/connect/img/image-placeholder.jpg";

const Shop = () => {
  const { t, i18n } = useTranslation();
  const { user } = useContext(UserContext);
  const { currentIndustry } = useIndustry();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categories, setCategories] = useState([]);
  const [userEventTickets, setUserEventTickets] = useState([]);
  const [scannedEventTicketIds, setScannedEventTicketIds] = useState([]);

  const companyProfiles = user?.employeeProfiles?.items?.filter(profile => profile.company) || [];

  const fetchEvents = async (currentIndustry) => {
      try {
          const eventData = await client.graphql({
              query: /* GraphQL */ `
              query ListIndustryEvents(
                $filter: ModelIndustryEventsFilterInput
                $limit: Int
              ) {
                listIndustryEvents(filter: $filter, limit: $limit) {
                  items {
                  event {
                    id
                    name {
                      language
                      text
                    }
                    image {
                      fileName
                      alt
                    }
                    location {
                      address {
                          city
                      }
                    }
                    ticketProduct {
                      id
                      name {
                        language
                        text
                      }
                      price
                    }
                    status
                    type
                    date
                    startDate
                    endDate
                    boothBooking
                    enableTicketBooking
                    landingPageUrl

                    createdAt
                    updatedAt
                    __typename
                  }
                  __typename
                }
              }
              }
            `,
              variables: { filter: { industryId: { eq: currentIndustry.id }} }
          });
          const eventList = eventData.data.listIndustryEvents.items.map(e => e.event).filter(e => e.status === "active").filter(e => new Date(e.startDate) >= new Date()).sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
          return eventList;
      } catch (error) {
          console.log('error on fetching events', error);
          captureError(error, {
              feature: 'shop',
              operation: 'fetch-events',
              consoleMessage: 'error on fetching events',
              context: {
                  industryId: currentIndustry?.id
              }
          });
          return [];
      }
  }

  const generateEventTicketProducts = (events) => {
    let products = [];
      events.forEach(event => {
          if (event.enableTicketBooking) {
              products.push({
                id: event.id,
                eventId: event.id,
                name: event.ticketProduct.name,
                type: 'ticket',
                price: event.ticketProduct.price,
                visibleInShop: true,
                productCategoryProductsId: event.type === "online_seminar" ? "ed2a0743-c014-4222-8075-c5c4ebf4d8e4" : 'd9bef73c-3da7-4041-b031-c01b4792400d',
                image: {
                  main: event.image
                },
                event: event
            });
          }
          if(event.boothBooking) {
              products.push({
                id: event.id,
                name: event.name,
                eventId: event.id,
                type: 'event_booth',
                price: null,
                visibleInShop: true,
                productCategoryProductsId: 'bc830bce-619f-4edb-814a-a7b6b0d57dee',
                image: {
                  main: event.image
                },
                event: event
            });
          }
      });
      return products;
  }

  const getCategories = async () => {
    try {
      const categoryRes = await client.graphql({
        query: /* GraphQL */ `
        query ListProductCategorys(
          $filter: ModelProductCategoryFilterInput
          $limit: Int
          $nextToken: String
        ) {
          listProductCategories(filter: $filter, limit: $limit, nextToken: $nextToken) {

            items {
              id
              name {
                language
                text
              }
              customerTypes
              description {
                language
                text
              }
              sortKey
            }
            nextToken
          }
        }
      `,
      });
      const categories = categoryRes.data.listProductCategories.items;
      setCategories([...categories].sort((a, b) => (a.sortKey ?? 0) - (b.sortKey ?? 0)));
    } catch (error) {
      console.warn("Error getting categories", error);
      captureError(error, {
          feature: 'shop',
          operation: 'fetch-product-categories',
          consoleMessage: 'Error getting categories'
      });
    }
  };

  const fetchProducts = async (nextToken = null, allProducts = [], currentIndustry) => {
    try {
      const productData = await client.graphql({
        query: `
          query ListProducts(
          $filter: ModelProductFilterInput
          $nextToken: String) {
            listProducts(filter: $filter, limit: 1000, nextToken: $nextToken) {
              items {
                id
                name {
                  text
                  language
                }
                shortDescription {
                  text
                  language
                }
                type
                price
                visibleInShop
                productCategoryProductsId
                availableInIndustries {
                  items {
                    industryId
                  }
                }
                image {
                  main {
                    fileName
                    alt
                  }
                }
                allowedBuyerGroups
              }
              nextToken
            }
          }
        `,
        variables: {
          filter: { visibleInShop: { eq: true } },
          nextToken }
      });

      const newProducts = productData.data.listProducts.items;
      let updatedProducts = [...allProducts, ...newProducts];

      if (productData.data.listProducts.nextToken) {
        // If there's a next token, fetch the next page
        return fetchProducts(productData.data.listProducts.nextToken, updatedProducts, currentIndustry);
      } else {
        const events = await fetchEvents(currentIndustry);
        updatedProducts = [
          ...updatedProducts.filter(product =>
            product.availableInIndustries.items.map(i => i.industryId).includes(currentIndustry.id))
            , ...generateEventTicketProducts(events)
          ];
        setProducts(updatedProducts);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setIsLoading(false);
      captureError(error, {
          feature: 'shop',
          operation: 'fetch-products',
          consoleMessage: 'Error fetching products:',
          context: {
              industryId: currentIndustry?.id
          }
      });
    }
  };

  const getUserEventTickets = async () => {
    try {
      const ticketsResponse = await client.graphql({
        query: /* GraphQL */ `
            query GetUser($id: ID!) {
                getUser(id: $id) {
                    id
                    owner
                    tickets {
                        items {
                            ticketEventId
                            paymentStatus
                            scans {
                                items {
                                    id
                                }
                            }
                            event {
                                id
                                name {
                                    language
                                    text
                                }
                                status
                                startDate
                                endDate
                                publicVideos
                                industries {
                                    items {
                                        industry {
                                            id
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `,
        variables: {
            id: user.id
        }
    });

    // Extrahiere die Events aus den Tickets
    const userTickets = ticketsResponse.data.getUser.tickets.items;
    const userEventTickets = userTickets.filter(ticket => ticket.event?.id).map(ticket => ticket.event.id)
    setUserEventTickets(userEventTickets);

    // Event-IDs mit bezahltem & gescanntem Ticket (qualifiziert für Rabatte)
    const scannedIds = userTickets
        .filter(t => t.paymentStatus === "paid" && t.event?.id && t.scans?.items?.length > 0)
        .map(t => t.event.id);
    setScannedEventTicketIds(scannedIds);
    } catch (error) {
      console.error('Error fetching user event tickets:', error);
      captureError(error, {
          feature: 'shop',
          operation: 'fetch-user-event-tickets',
          consoleMessage: 'Error fetching user event tickets:',
          context: {
              userId: user?.id
          }
      });
    }
  };

  useEffect(() => {
    if(currentIndustry?.id) {
    setIsLoading(true);
    fetchProducts(null, [], currentIndustry);
    getCategories();
    }

  }, [currentIndustry]);

  useEffect(() => {
    if (user?.employeeProfiles?.items?.length > 0 && !selectedCompany) {
      setSelectedCompany(user.employeeProfiles.items.find(profile => profile.company)?.company || null);
    }
    if(user.id) {
      getUserEventTickets();
    }
  }, [user]);

  // Zielgruppen-Targeting über allowedBuyerGroups (event_<id> / not_event_<id>)
  const isAllowedForUser = (product) => {
    const productEventFilter = product.allowedBuyerGroups?.find(group => group.startsWith("event_"))?.replace("event_", "");
    if (productEventFilter) {
      // Events mit Scan-Pflicht: zusätzlicher Check gegen scannedEventTicketIds.
      if (SCAN_REQUIRED_EVENT_IDS.includes(productEventFilter)) {
        return scannedEventTicketIds.includes(productEventFilter);
      }
      if(userEventTickets.length === 0) return false;
      return userEventTickets.includes(productEventFilter);
    }
    const productEventHideFilter = product.allowedBuyerGroups?.find(group => group.includes("not_event_"))?.replace("not_event_", "");
    if (productEventHideFilter) {
      // Symmetrisch: bei Scan-Pflicht-Events zählt nur ein gescanntes Ticket
      // als "Besucher" — wer Ticket ohne Scan hat, gilt als Nicht-Besucher.
      if (SCAN_REQUIRED_EVENT_IDS.includes(productEventHideFilter)) {
        return !scannedEventTicketIds.includes(productEventHideFilter);
      }
      if (userEventTickets.length === 0) return true;
      return !userEventTickets.includes(productEventHideFilter);
    }
    return true;
  };

  const categoryById = useMemo(() => Object.fromEntries(categories.map(category => [category.id, category])), [categories]);
  const audiencesOf = (product) => (categoryById[product.productCategoryProductsId]?.customerTypes || []).filter(type => AUDIENCES.includes(type));

  // Alle Produkte, die der User sehen darf und die in einer Shop-Kategorie liegen
  const shopProducts = products.filter(product => audiencesOf(product).length > 0 && isAllowedForUser(product));

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const matchesSearch = (product) => !normalizedSearch || [
    getLocalizedText(product.name),
    stripHtml(getLocalizedText(product.shortDescription)),
    getLocalizedText(categoryById[product.productCategoryProductsId]?.name),
  ].join(" ").toLowerCase().includes(normalizedSearch);

  const filteredProducts = shopProducts
    .filter(product => selectedCategory === null || product.productCategoryProductsId === selectedCategory)
    .filter(matchesSearch);

  // Kategorien als Filter-Chips: nur Kategorien mit Produkten, gruppiert nach erster Zielgruppe
  const countInCategory = (categoryId) => shopProducts.filter(product => product.productCategoryProductsId === categoryId).length;
  const chipGroups = AUDIENCES.map(audience => ({
    audience,
    categories: categories.filter(category => {
      const types = (category.customerTypes || []).filter(type => AUDIENCES.includes(type));
      return types[0] === audience && countInCategory(category.id) > 0;
    }),
  })).filter(group => group.categories.length > 0);

  const sectionConfig = {
    consumer: { title: t('For you'), icon: 'user' },
    business: { title: t('For your company'), icon: 'building' },
  };
  const sections = AUDIENCES.map(audience => ({
    audience,
    items: filteredProducts.filter(product => audiencesOf(product).includes(audience)),
    categoryNames: categories
      .filter(category => category.customerTypes?.includes(audience) && countInCategory(category.id) > 0)
      .map(category => getLocalizedText(category.name)),
  })).filter(section => section.items.length > 0);

  const resetFilters = () => {
    setSelectedCategory(null);
    setSearchTerm("");
  };

  const renderCompanyContext = () => {
    if (companyProfiles.length === 0) {
      return (
        <span className="small shop-meta">
          <MDBIcon fas icon="info-circle" className="me-1" />
          {t('To book offers for companies, you need an employee profile at a company.')}
        </span>
      );
    }
    const companyLabel = (company) => (
      <span className="d-inline-flex align-items-center gap-2">
        <img src={companyImageUrl(company)} className="shop-company-logo rounded bg-white border" alt="" />
        <span className="fw-bold text-truncate">{company?.name}</span>
      </span>
    );
    return (
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <span className="small shop-meta">{t('Booked for')}</span>
        {companyProfiles.length === 1 ? (
          <span className="shop-company-static border rounded px-2">{companyLabel(selectedCompany)}</span>
        ) : (
          <MDBDropdown>
            <MDBDropdownToggle color="white" className="shop-company-toggle border text-dark shadow-0 px-2">
              {companyLabel(selectedCompany)}
            </MDBDropdownToggle>
            <MDBDropdownMenu>
              {companyProfiles.map((profile) => (
                <MDBDropdownItem
                  key={profile.company.id}
                  link
                  onClick={() => setSelectedCompany(profile.company)}
                  className={selectedCompany?.id === profile.company.id ? "active" : ""}
                >
                  {profile.company.name}
                </MDBDropdownItem>
              ))}
            </MDBDropdownMenu>
          </MDBDropdown>
        )}
      </div>
    );
  };

  const renderPrice = (product) => {
    if (product.type === "event_booth") {
      return <span className="small shop-meta">{t('Price depends on booth selection')}</span>;
    }
    // Event-Tickets ohne Preis am ticketProduct sind kostenlos (bisher als "0,00 €" angezeigt)
    if (product.price == null && product.type !== "ticket") return <span />;
    if (!product.price) {
      return <h5 className="mb-0 text-success">{t('Free')}</h5>;
    }
    return <h5 className="mb-0">{transformMoney(product.price)}</h5>;
  };

  const renderAction = (product) => {
    if (product.type === "ticket" && userEventTickets.includes(product.eventId)) {
      return (
        <div className="text-end">
          <div className="small fw-bold text-success"><MDBIcon fas icon="check-circle" className="me-1" />{t('Booked')}</div>
          <Link to="/bookings" className="small text-secondary">{t('View booking')}</Link>
        </div>
      );
    }
    if (product.type === "event_booth") {
      return selectedCompany ? (
        <Link to={`/mycompany/${selectedCompany.id}/select-booth/${product.id}`} className="btn btn-primary btn-sm shop-btn">
          {t('Select booth')}
        </Link>
      ) : (
        <MDBBtn size="sm" className="shop-btn" disabled>{t('Select booth')}</MDBBtn>
      );
    }
    if (product.type === "ticket") {
      return (
        <Link to={`/book-ticket?eventId=${product.id}`} className="btn btn-primary btn-sm shop-btn">
          {t('Book ticket')}
        </Link>
      );
    }
    return (
      <Link to={`/checkout?productId=${product.id}`} className="btn btn-primary btn-sm shop-btn">
        {t('Book now')}
      </Link>
    );
  };

  const renderProductCard = (product) => {
    const category = categoryById[product.productCategoryProductsId];
    const shortDescription = stripHtml(getLocalizedText(product.shortDescription));
    const eventDates = formatEventDates(product.event, i18n.language);
    const eventCity = product.event?.location?.address?.city;
    const isEventImage = product.type === "ticket" || product.type === "event_booth";
    return (
      <MDBCol key={`${product.type}-${product.id}`} sm="6" lg="4" xxl="3" className="mb-4">
        <MDBCard className="h-100 shop-card">
          <MDBCardImage
            src={product.image?.main?.fileName
              ? `https://iec-cp-public.s3.eu-central-1.amazonaws.com/${isEventImage ? "EventImages" : "ProductImages"}/${product.image.main.fileName}`
              : "https://placehold.co/320x180?text=+"}
            alt={product.image?.main?.alt || getLocalizedText(product.name)}
            className="shop-card-image"
          />
          <MDBCardBody className="d-flex flex-column">
            {category && <div className="shop-card-category shop-meta mb-1">{getLocalizedText(category.name)}</div>}
            <MDBCardTitle className="shop-card-title">
              {(product.type === "event_booth" ? t("Booth") + " " : "") + getLocalizedText(product.name)}
            </MDBCardTitle>
            {eventDates && (
              <div className="small shop-meta mb-1">
                <MDBIcon far icon="calendar" className="me-1" />{eventDates}{eventCity ? ` · ${eventCity}` : ""}
              </div>
            )}
            {shortDescription && <MDBCardText className="small shop-meta shop-card-desc">{shortDescription}</MDBCardText>}
            <div className="mt-auto pt-2 d-flex justify-content-between align-items-end gap-2 flex-wrap">
              {renderPrice(product)}
              {renderAction(product)}
            </div>
          </MDBCardBody>
        </MDBCard>
      </MDBCol>
    );
  };

  const hasActiveFilter = selectedCategory !== null || normalizedSearch !== "";

  return (
    <div className="shop">
      <div className="d-flex justify-content-between align-items-end flex-wrap gap-3 mb-3">
        <div>
          <h4 className="mb-1">{t('Shop')}</h4>
          <p className="shop-meta mb-0">{t('Tickets, recordings and offers for exhibitors')}</p>
        </div>
        <Link to="/bookings" className="btn btn-outline-primary btn-sm shop-btn">
          <MDBIcon fas icon="shopping-bag" className="me-2" />{t('My Bookings')}
        </Link>
      </div>

      <MDBCard className="shop-filters mb-3">
        <MDBCardBody className="py-3">
          <div className="shop-search mb-3">
            <MDBInput
              type="search"
              label={t('Search shop')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {chipGroups.length > 0 && (
            <div className="d-flex flex-wrap align-items-center gap-2">
              <MDBBtn
                rounded
                size="sm"
                color={selectedCategory === null ? "primary" : "light"}
                className={"shop-chip shadow-0" + (selectedCategory === null ? "" : " border")}
                onClick={() => setSelectedCategory(null)}
              >
                {t('All offers')} <span className="shop-chip-count">{shopProducts.length}</span>
              </MDBBtn>
              {chipGroups.map(group => (
                <React.Fragment key={group.audience}>
                  <span className="shop-chip-divider border-start" />
                  {group.audience === "business" && (
                    <span className="shop-chip-label shop-meta">{t('Companies')}</span>
                  )}
                  {group.categories.map(category => (
                    <MDBBtn
                      key={category.id}
                      rounded
                      size="sm"
                      color={selectedCategory === category.id ? "primary" : "light"}
                      className={"shop-chip shadow-0" + (selectedCategory === category.id ? "" : " border")}
                      onClick={() => setSelectedCategory(selectedCategory === category.id ? null : category.id)}
                    >
                      {getLocalizedText(category.name)} <span className="shop-chip-count">{countInCategory(category.id)}</span>
                    </MDBBtn>
                  ))}
                </React.Fragment>
              ))}
            </div>
          )}
        </MDBCardBody>
      </MDBCard>

      {hasActiveFilter && (
        <div className="d-flex align-items-center gap-3 small shop-meta mt-3">
          <span>{t('{{number}} offers', { number: filteredProducts.length })}</span>
          <MDBBtn color="link" size="sm" className="shop-btn px-0 text-secondary" onClick={resetFilters}>{t('Reset filters')}</MDBBtn>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-5"><MDBSpinner role="status"><span className="visually-hidden">{t('Loading...')}</span></MDBSpinner></div>
      ) : sections.length === 0 ? (
        <div className="text-center shop-meta border rounded py-5 px-3 mt-4">
          <p className="mb-0">{hasActiveFilter ? t('No offers match your filters.') : t('There are currently no offers in the shop.')}</p>
        </div>
      ) : sections.map(section => (
        <section key={section.audience} className="mt-4">
          <div className="d-flex justify-content-between align-items-end flex-wrap gap-2 border-bottom pb-2 mb-3">
            <div>
              <h5 className="mb-0">
                <MDBIcon fas icon={sectionConfig[section.audience].icon} className="text-secondary me-2" />
                {sectionConfig[section.audience].title}
              </h5>
              <div className="small shop-meta">{section.categoryNames.join(", ")}</div>
            </div>
            {section.audience === "business" && renderCompanyContext()}
          </div>
          <MDBRow>
            {section.items.map(renderProductCard)}
          </MDBRow>
        </section>
      ))}
    </div>
  );
};

export default Shop;
